import type { Collector, Signal } from './types';
import { safe, signal, unavailable } from './util';

/**
 * First value of a media feature that matches. Returns null when the browser doesn't recognise the
 * feature at all (matchMedia reports such queries as "not all").
 */
export function firstMatch(feature: string, values: readonly string[]): string | null {
  if (typeof matchMedia !== 'function') return null;
  for (const v of values) {
    const mq = matchMedia(`(${feature}: ${v})`);
    if (mq.media === 'not all') return null;
    if (mq.matches) return v;
  }
  return 'none matched';
}

function feature(id: string, label: string, name: string, values: readonly string[]): Promise<Signal> {
  return safe(id, label, () => {
    const v = firstMatch(name, values);
    return v === null ? unavailable(id, label, `${name} is not supported in this browser`) : signal(id, label, v);
  });
}

function scrollbarWidth(): number {
  const outer = document.createElement('div');
  outer.style.setProperty('position', 'absolute');
  outer.style.setProperty('left', '-9999px');
  outer.style.setProperty('width', '100px');
  outer.style.setProperty('height', '100px');
  outer.style.setProperty('overflow', 'scroll');
  document.body.appendChild(outer);
  try {
    return outer.offsetWidth - outer.clientWidth;
  } finally {
    outer.remove();
  }
}

function computedFontSize(styles: Record<string, string>): string {
  const el = document.createElement('span');
  for (const [k, v] of Object.entries(styles)) el.style.setProperty(k, v);
  el.textContent = 'x';
  document.body.appendChild(el);
  try {
    return getComputedStyle(el).fontSize;
  } finally {
    el.remove();
  }
}

export const cssMediaCollector: Collector = {
  id: 'css',
  title: 'CSS media & preferences',
  description: 'Display and accessibility preferences that any stylesheet can read, plus scrollbar and default font sizes.',
  async collect() {
    if (typeof matchMedia !== 'function' || typeof document === 'undefined' || !document.body)
      return [unavailable('css.colorScheme', 'Color scheme', 'matchMedia is not available')];

    return Promise.all([
      feature('css.colorScheme', 'Color scheme', 'prefers-color-scheme', ['dark', 'light']),
      feature('css.reducedMotion', 'Reduced motion', 'prefers-reduced-motion', ['reduce', 'no-preference']),
      feature('css.reducedTransparency', 'Reduced transparency', 'prefers-reduced-transparency', ['reduce', 'no-preference']),
      feature('css.contrast', 'Contrast preference', 'prefers-contrast', ['more', 'less', 'custom', 'no-preference']),
      feature('css.forcedColors', 'Forced colors', 'forced-colors', ['active', 'none']),
      feature('css.invertedColors', 'Inverted colors', 'inverted-colors', ['inverted', 'none']),
      safe('css.input', 'Pointer & hover', () => {
        const value = {
          hover: firstMatch('hover', ['hover', 'none']),
          anyHover: firstMatch('any-hover', ['hover', 'none']),
          pointer: firstMatch('pointer', ['fine', 'coarse', 'none']),
          anyPointer: firstMatch('any-pointer', ['fine', 'coarse', 'none']),
        };
        return signal('css.input', 'Pointer & hover', value, {
          display: `hover: ${value.hover} · any-hover: ${value.anyHover} · pointer: ${value.pointer} · any-pointer: ${value.anyPointer}`,
        });
      }),
      // Ordered widest first: a P3 display also matches srgb.
      feature('css.colorGamut', 'Color gamut', 'color-gamut', ['rec2020', 'p3', 'srgb']),
      feature('css.dynamicRange', 'Dynamic range (HDR)', 'dynamic-range', ['high', 'standard']),
      feature('css.displayMode', 'Display mode', 'display-mode', ['fullscreen', 'standalone', 'minimal-ui', 'window-controls-overlay', 'browser']),
      safe('css.scrollbar', 'Scrollbar width', () => {
        const w = scrollbarWidth();
        return signal('css.scrollbar', 'Scrollbar width', w, { display: w === 0 ? '0 px (overlay scrollbars)' : `${w} px` });
      }),
      safe('css.fontSizes', 'Default font sizes', () => {
        const value = {
          rem: getComputedStyle(document.documentElement).fontSize,
          medium: computedFontSize({ 'font-size': 'medium', 'font-family': 'serif' }),
          monospace: computedFontSize({ 'font-size': 'medium', 'font-family': 'monospace' }),
        };
        return signal('css.fontSizes', 'Default font sizes', value, {
          display: `1rem = ${value.rem} · default = ${value.medium} · monospace default = ${value.monospace}`,
        });
      }),
    ]);
  },
};
